import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { wsPath } from "../lib/workspace";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ClipboardText, Plus, ArrowRight, Copy, QrCode, Trash, PushPin, PushPinSlash, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TenantReportFormsPage() {
  const [forms, setForms] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({
    is_generic: false,
    client_id: "",
    display_name: "",
    name: "",
    mode: "client_wide",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [fRes, cRes] = await Promise.all([
        API.get("/tenant-report-forms"),
        API.get("/clients?limit=200"),
      ]);
      setForms(fRes.data.forms || []);
      const clientList = Array.isArray(cRes.data) ? cRes.data : cRes.data.clients || [];
      setClients(clientList);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load forms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createForm = async () => {
    if (!newForm.is_generic && !newForm.client_id) {
      toast.error("Choose a client, or switch to Generic");
      return;
    }
    if (newForm.is_generic && !newForm.display_name.trim()) {
      toast.error("Give the generic form a display name");
      return;
    }
    setCreating(true);
    try {
      const payload = newForm.is_generic
        ? {
            is_generic: true,
            display_name: newForm.display_name.trim(),
            name: newForm.name || `${newForm.display_name.trim()} — Maintenance Reports`,
            mode: newForm.mode,
          }
        : {
            client_id: newForm.client_id,
            name: newForm.name,
            mode: newForm.mode,
          };
      const res = await API.post("/tenant-report-forms", payload);
      toast.success("Form created");
      setShowCreate(false);
      setNewForm({ is_generic: false, client_id: "", display_name: "", name: "", mode: "client_wide" });
      window.location.href = wsPath(`/tenant-report-forms/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const deleteForm = async (fid, name) => {
    if (!window.confirm(`Delete '${name}'?\n\nExisting submissions will remain in Jobs/Job Requests/Emergency Jobs but the public link will stop working.`)) return;
    try {
      await API.delete(`/tenant-report-forms/${fid}`);
      toast.success("Form deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const copyPublicUrl = (token) => {
    const url = `${window.location.origin}/report-issue/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Public link copied");
  };

  const togglePin = async (f) => {
    try {
      await API.put(`/tenant-report-forms/${f.id}`, { pinned: !f.pinned });
      toast.success(f.pinned ? "Unpinned" : "Pinned to top");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Pin failed");
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Loading…</div>;

  const pinned = forms.filter((f) => f.pinned);
  const unpinned = forms.filter((f) => !f.pinned);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="tenant-report-forms-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Tenant Report Forms</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Create branded maintenance-reporting forms for each property manager. Tenants submit issues directly; reports auto-route by severity.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="trf-new-form-btn">
          <Plus className="h-4 w-4 mr-2" /> New form
        </Button>
      </div>

      {pinned.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {pinned.map((f) => {
            const publicUrl = `${window.location.origin}/report-issue/${f.token}`;
            return (
              <div key={f.id} className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5 flex gap-4 items-start" data-testid={`trf-pinned-${f.id}`}>
                <img src={`${BACKEND}/api/tenant-report-forms/${f.id}/qr`}
                     alt={`QR for ${f.name}`} className="w-32 h-32 bg-white rounded-md p-1 border border-amber-200 shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-start gap-2">
                    <PushPin weight="fill" className="h-4 w-4 text-amber-600 shrink-0 mt-1" />
                    <div className="min-w-0">
                      <p className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate">{f.name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {f.is_generic ? `Generic · ${f.client_company || "Direct tenants"}` : (f.client_company || f.client_name)}
                      </p>
                    </div>
                  </div>
                  <p className="text-[10px] font-mono text-slate-500 break-all">{publicUrl}</p>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Button size="sm" variant="outline" onClick={() => copyPublicUrl(f.token)} data-testid={`trf-pinned-copy-${f.id}`}>
                      <Copy className="h-3 w-3 mr-1" /> Link
                    </Button>
                    <a href={`${BACKEND}/api/tenant-report-forms/${f.id}/qr`} target="_blank" rel="noreferrer" download={`${f.name}.png`}>
                      <Button size="sm" variant="outline"><QrCode className="h-3 w-3 mr-1" /> QR</Button>
                    </a>
                    <Link to={`/tenant-report-forms/${f.id}/poster`}>
                      <Button size="sm" variant="outline" data-testid={`trf-pinned-poster-${f.id}`}>
                        <Printer className="h-3 w-3 mr-1" /> Poster
                      </Button>
                    </Link>
                    <Link to={`/tenant-report-forms/${f.id}`}>
                      <Button size="sm" data-testid={`trf-pinned-open-${f.id}`}>Open <ArrowRight className="h-3 w-3 ml-1" /></Button>
                    </Link>
                    <button onClick={() => togglePin(f)} className="text-amber-700 hover:text-amber-900 px-2" title="Unpin" data-testid={`trf-unpin-${f.id}`}>
                      <PushPinSlash className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {forms.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center">
          <ClipboardText className="h-10 w-10 text-slate-400 mx-auto mb-3" />
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-1 font-medium">No tenant report forms yet</p>
          <p className="text-xs text-slate-500 mb-4">Create one per client and share the public link or QR code with their tenants.</p>
          <Button onClick={() => setShowCreate(true)} data-testid="trf-empty-create-btn">Create your first form</Button>
        </div>
      ) : unpinned.length === 0 ? null : (
        <div className="grid gap-3">
          {unpinned.map((f) => (
            <div key={f.id} className="bg-card border border-border rounded-lg p-4 flex items-center gap-4 flex-wrap" data-testid={`trf-row-${f.id}`}>
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-slate-900 dark:text-slate-100">{f.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {f.is_generic ? (
                    <span className="inline-flex items-center gap-1">
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-700 font-medium">GENERIC</span>
                      {f.client_company || "Direct tenants"}
                    </span>
                  ) : (
                    f.client_company || f.client_name || "—"
                  )}
                  {" · "}
                  {f.mode === "property" ? "Single property" : "All properties"}
                </p>
                <p className="text-[11px] text-slate-400 mt-1">
                  {f.submission_count || 0} submission{f.submission_count === 1 ? "" : "s"} · created {(f.created_at || "").slice(0, 10)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => togglePin(f)} className="text-slate-400 hover:text-amber-600 p-1.5" title="Pin to top of dashboard" data-testid={`trf-pin-${f.id}`}>
                  <PushPin className="h-3.5 w-3.5" />
                </button>
                <Button size="sm" variant="outline" onClick={() => copyPublicUrl(f.token)} data-testid={`trf-copy-${f.id}`}>
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
                </Button>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tenant-report-forms/${f.id}/qr`} target="_blank" rel="noreferrer" download={`${f.name}.png`}>
                  <Button size="sm" variant="outline" data-testid={`trf-qr-${f.id}`}>
                    <QrCode className="h-3.5 w-3.5 mr-1" /> QR
                  </Button>
                </a>
                <Link to={`/tenant-report-forms/${f.id}`}>
                  <Button size="sm" data-testid={`trf-open-${f.id}`}>
                    Open <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </Link>
                <button onClick={() => deleteForm(f.id, f.name)} className="text-red-500 hover:text-red-700 p-1.5" title="Delete" data-testid={`trf-delete-${f.id}`}>
                  <Trash className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="trf-create-dialog">
          <DialogHeader><DialogTitle>New tenant report form</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Form type</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button"
                        onClick={() => setNewForm({ ...newForm, is_generic: false })}
                        className={`text-left border rounded-md p-3 transition ${!newForm.is_generic ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"}`}
                        data-testid="trf-type-client">
                  <p className="text-sm font-semibold">Specific client</p>
                  <p className="text-[11px] text-slate-500 mt-1">Tied to a property manager — submissions auto-link to them.</p>
                </button>
                <button type="button"
                        onClick={() => setNewForm({ ...newForm, is_generic: true })}
                        className={`text-left border rounded-md p-3 transition ${newForm.is_generic ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"}`}
                        data-testid="trf-type-generic">
                  <p className="text-sm font-semibold">Generic</p>
                  <p className="text-[11px] text-slate-500 mt-1">No specific client — for your direct tenants or shared links. Pick a client later.</p>
                </button>
              </div>
            </div>

            {newForm.is_generic ? (
              <div>
                <Label>Display name *</Label>
                <Input value={newForm.display_name}
                       onChange={(e) => setNewForm({ ...newForm, display_name: e.target.value })}
                       placeholder="e.g. Direct Tenants"
                       className="mt-1" data-testid="trf-display-name" />
                <p className="text-[11px] text-slate-500 mt-1">Shown as the heading on the public form.</p>
              </div>
            ) : (
              <div>
                <Label>Client (property manager) *</Label>
                <Select value={newForm.client_id} onValueChange={(v) => setNewForm({ ...newForm, client_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="trf-client-select"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || c.id.slice(0, 8)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Form name (optional)</Label>
              <Input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                     placeholder="Defaults to '<Display> — Tenant Reports'" className="mt-1"
                     data-testid="trf-name-input" />
            </div>
            <div>
              <Label>Scope</Label>
              <Select value={newForm.mode} onValueChange={(v) => setNewForm({ ...newForm, mode: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client_wide">Tenants type the address</SelectItem>
                  <SelectItem value="property">Single property (no address typing)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createForm} disabled={creating} data-testid="trf-create-confirm">
              {creating ? "Creating…" : "Create form"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
